import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ExperimentContext } from './ExperimentContext';
import type { ExperimentContextType } from '../../experiments/types';
import type { ExperimentCode } from '../../experiments/experiments';
import { GoogleAnalyticsHelpers } from '../../googleAnalytics/GoogleAnalyticsHelpers';
import { logEvent } from '../../utils/logger-utils';

export const ExperimentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [assignments, setAssignments] = useState<Map<string, string>>(new Map());
  const [initialized, setInitialized] = useState(false);
  const exposed = useRef<Set<string>>(new Set());

  const initialize = useCallback((map: Map<string, string>) => {
    setAssignments(map);
    setInitialized(true);
  }, []);

  const getVariant = useCallback(
    (code: ExperimentCode): string | undefined => assignments.get(code),
    [assignments]
  );

  const fireExposure = useCallback((code: ExperimentCode, variant: string) => {
    const key = `${code}:${variant}`;
    if (exposed.current.has(key)) return;
    exposed.current.add(key);
    try {
      GoogleAnalyticsHelpers.UpdateDataLayer({
        category: 'Experiment',
        action: 'Exposure',
        experimentCode: code,
        variantCode: variant,
      });
    } catch {
      // analytics must never break the payment flow
    }
    logEvent({
      message: 'Experiment Exposure',
      level: 'info',
      extra: { experimentCode: code, variantCode: variant },
    });
  }, []);

  const value = useMemo<ExperimentContextType>(
    () => ({ getVariant, initialize, initialized, fireExposure }),
    [getVariant, initialize, initialized, fireExposure]
  );

  return <ExperimentContext.Provider value={value}>{children}</ExperimentContext.Provider>;
};
